import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Search, Eye, CheckCircle2, Ban, Package, X, Loader2, Printer } from 'lucide-react';
import { ordenesEntregaService } from '../../../services/api';
import useModal from '../../../hooks/useModal';
import OrdenEntregaPrintModal from './OrdenEntregaPrintModal';

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  en_despacho: 'En despacho',
  entregada_parcial: 'Entregada parcial',
  entregada: 'Entregada',
  anulada: 'Anulada'
};

const ESTADO_COLOR = {
  pendiente: 'bg-slate-100 text-slate-700',
  en_despacho: 'bg-brand-100 text-brand-800',
  entregada_parcial: 'bg-amber-100 text-amber-800',
  entregada: 'bg-emerald-100 text-emerald-800',
  anulada: 'bg-red-100 text-red-700'
};

const OrdenesEntregaView = () => {
  const { notify, confirm, prompt, ModalRenderer } = useModal();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [modo, setModo] = useState(null); // 'ver' | 'confirmar'
  const [imprimirOrden, setImprimirOrden] = useState(null); // { ordenId, confirmacionId? }

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (estadoFiltro) params.estado = estadoFiltro;
      const response = await ordenesEntregaService.getAll(params);
      setOrdenes(response.data || []);
    } catch (error) {
      await notify('error', 'Error', error.message || 'No se pudieron cargar las órdenes de entrega');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (orden, modoAbrir) => {
    try {
      const response = await ordenesEntregaService.getById(orden.id);
      setOrdenSeleccionada(response.data);
      setModo(modoAbrir);
    } catch (error) {
      await notify('error', 'Error', error.message || 'No se pudo cargar el detalle');
    }
  };

  const handleAnular = async (orden) => {
    const motivo = await prompt('Anular orden de entrega', `¿Por qué se anula ${orden.numero}?`, 'Motivo', 'Mínimo 5 caracteres...');
    if (!motivo || motivo.trim().length < 5) {
      if (motivo !== false) await notify('warning', 'Motivo requerido', 'El motivo debe tener al menos 5 caracteres');
      return;
    }
    let revertirStock = false;
    if (['entregada_parcial', 'entregada'].includes(orden.estado)) {
      revertirStock = await confirm('Revertir stock', '¿Deseas devolver al inventario lo ya entregado en esta orden?');
    }
    try {
      const res = await ordenesEntregaService.anular(orden.id, { motivo, revertir_stock: revertirStock });
      await notify('success', 'Listo', res.message);
      cargar();
    } catch (error) {
      await notify('error', 'Error', error.message || 'No se pudo anular la orden');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Órdenes de Entrega</h2>
          <p className="text-sm text-slate-500 mt-0.5">Se generan solas al pagar una cotización</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <div className="relative sm:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por número, cliente, empresa o solicitud..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        <select
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
              <th className="px-4 py-2.5">Número</th>
              <th className="px-4 py-2.5">Cliente / Empresa</th>
              <th className="px-4 py-2.5">Cotización</th>
              <th className="px-4 py-2.5">Solicitud</th>
              <th className="px-4 py-2.5">Ítems</th>
              <th className="px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400"><Loader2 className="inline animate-spin" size={18} /></td></tr>
            ) : ordenes.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin órdenes de entrega</td></tr>
            ) : ordenes.map(orden => (
              <tr key={orden.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-medium text-slate-900">{orden.numero}</td>
                <td className="px-4 py-2.5 text-slate-700">{orden.cliente_nombre || orden.empresa_nombre || '-'}</td>
                <td className="px-4 py-2.5 text-slate-500">{orden.cotizacion_numero}</td>
                <td className="px-4 py-2.5 text-slate-500">{orden.solicitud_folio || '-'}</td>
                <td className="px-4 py-2.5 text-slate-500">{orden.items_completos}/{orden.total_items}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ESTADO_COLOR[orden.estado]}`}>
                    {ESTADO_LABEL[orden.estado]}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => abrirDetalle(orden, 'ver')} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" title="Ver">
                      <Eye size={16} />
                    </button>
                    <button onClick={() => setImprimirOrden({ ordenId: orden.id })} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" title="Imprimir">
                      <Printer size={16} />
                    </button>
                    {['pendiente', 'en_despacho', 'entregada_parcial'].includes(orden.estado) && (
                      <button onClick={() => abrirDetalle(orden, 'confirmar')} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Confirmar entrega">
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                    {orden.estado !== 'anulada' && (
                      <button onClick={() => handleAnular(orden)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Anular">
                        <Ban size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ordenSeleccionada && modo === 'ver' && (
        <DetalleModal
          orden={ordenSeleccionada}
          onClose={() => { setOrdenSeleccionada(null); setModo(null); }}
          onImprimir={setImprimirOrden}
        />
      )}
      {ordenSeleccionada && modo === 'confirmar' && (
        <ConfirmarEntregaModal
          orden={ordenSeleccionada}
          onClose={() => { setOrdenSeleccionada(null); setModo(null); }}
          onSuccess={() => { setOrdenSeleccionada(null); setModo(null); cargar(); }}
          notify={notify}
        />
      )}
      {imprimirOrden && (
        <OrdenEntregaPrintModal
          ordenId={imprimirOrden.ordenId}
          confirmacionId={imprimirOrden.confirmacionId}
          onClose={() => setImprimirOrden(null)}
        />
      )}
      {ModalRenderer}
    </div>
  );
};

const DetalleModal = ({ orden, onClose, onImprimir }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">{orden.numero}</h3>
          <p className="text-xs text-slate-500">{ESTADO_LABEL[orden.estado]}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onImprimir({ ordenId: orden.id })} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" title="Imprimir orden completa">
            <Printer size={18} />
          </button>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-xs text-slate-400">Cliente</p><p className="text-slate-700">{orden.cliente_nombre || orden.empresa_nombre || '-'}</p></div>
          <div><p className="text-xs text-slate-400">RIF/CI</p><p className="text-slate-700">{orden.cliente_rif || orden.cliente_cedula || '-'}</p></div>
          <div><p className="text-xs text-slate-400">Cotización</p><p className="text-slate-700">{orden.cotizacion_numero}</p></div>
          <div><p className="text-xs text-slate-400">Solicitud</p><p className="text-slate-700">{orden.solicitud_folio || '-'}</p></div>
          <div><p className="text-xs text-slate-400">Vía</p><p className="text-slate-700">{orden.via_entrega || '-'}</p></div>
          <div><p className="text-xs text-slate-400">Fecha estimada</p><p className="text-slate-700">{orden.fecha_estimada_entrega ? new Date(orden.fecha_estimada_entrega).toLocaleDateString('es-VE') : '-'}</p></div>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5"><Package size={14} /> Ítems</p>
          <div className="space-y-1.5">
            {orden.items?.map(item => (
              <div key={item.id} className="flex justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                <span className="text-slate-700">{item.descripcion} {item.es_servicio && <span className="text-xs text-slate-400">(servicio)</span>}</span>
                <span className="text-slate-500">{item.cantidad_entregada} / {item.cantidad_pedida} {item.unidad}</span>
              </div>
            ))}
          </div>
        </div>
        {orden.confirmaciones?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Entregas confirmadas</p>
            <div className="space-y-1.5">
              {orden.confirmaciones.map(c => (
                <div key={c.id} className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                  <span>{new Date(c.fecha_real).toLocaleString('es-VE')} · Recibió: {c.receptor_nombre} ({c.receptor_cedula})</span>
                  <button onClick={() => onImprimir({ ordenId: orden.id, confirmacionId: c.id })} className="text-slate-400 hover:text-slate-700 p-1" title="Imprimir recibo de esta entrega">
                    <Printer size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {orden.motivo_anulacion && (
          <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">Motivo de anulación: {orden.motivo_anulacion}</div>
        )}
      </div>
    </div>
  </div>
);

const ConfirmarEntregaModal = ({ orden, onClose, onSuccess, notify }) => {
  const [cantidades, setCantidades] = useState(() => {
    const inicial = {};
    (orden.items || []).forEach(it => { inicial[it.id] = ''; });
    return inicial;
  });
  const [receptorNombre, setReceptorNombre] = useState('');
  const [receptorCedula, setReceptorCedula] = useState('');
  const [guardando, setGuardando] = useState(false);

  const itemsPendientes = (orden.items || []).filter(it => parseFloat(it.cantidad_entregada) < parseFloat(it.cantidad_pedida));

  const handleGuardar = async () => {
    if (!receptorNombre.trim() || !receptorCedula.trim()) {
      await notify('error', 'Atención', 'Nombre y cédula de quien recibe son requeridos');
      return;
    }
    const entregas = itemsPendientes
      .map(it => ({ orden_entrega_item_id: it.id, cantidad: parseFloat(cantidades[it.id]) }))
      .filter(e => e.cantidad > 0);
    if (entregas.length === 0) {
      await notify('error', 'Atención', 'Indica la cantidad entregada de al menos un ítem');
      return;
    }
    setGuardando(true);
    try {
      const res = await ordenesEntregaService.confirmarEntrega(orden.id, {
        entregas, receptor_nombre: receptorNombre.trim(), receptor_cedula: receptorCedula.trim()
      });
      await notify('success', 'Listo', res.message);
      onSuccess();
    } catch (error) {
      await notify('error', 'Error', error.message || 'No se pudo confirmar la entrega');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Confirmar entrega — {orden.numero}</h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            {itemsPendientes.map(item => {
              const pendiente = parseFloat(item.cantidad_pedida) - parseFloat(item.cantidad_entregada);
              return (
                <div key={item.id} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 truncate">{item.descripcion}</p>
                    <p className="text-xs text-slate-400">Pendiente: {pendiente} {item.unidad}</p>
                  </div>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    max={pendiente}
                    value={cantidades[item.id]}
                    onChange={(e) => setCantidades(prev => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="0"
                    className="w-24 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
              );
            })}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Recibido por *</label>
            <input value={receptorNombre} onChange={(e) => setReceptorNombre(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Cédula de quien recibe *</label>
            <input value={receptorCedula} onChange={(e) => setReceptorCedula(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 flex items-center gap-2">
            {guardando && <Loader2 size={14} className="animate-spin" />} Confirmar entrega
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdenesEntregaView;
